<template>
  <div class="i-personality">
    <section id="professional_bio">
      <iProfessionalBio />
    </section>

    <section id="personality_analysis">
      <iPersonalityAnalysis />
    </section>

    <section
      v-if="reportStore.hasStrengthItems || reportStore.hasWeaknessItems"
      id="strengths_weaknesses"
    >
      <iStrengthsAndWeaknesses />
    </section>

    <section id="bonus">
      <iSelfImprovement />
    </section>

    <section
      v-if="reportStore.hasWorkplaceTraitsItems"
      id="workplace_traits"
    >
      <iWorkplaceTraits />
    </section>

    <section
      v-if="!userStore.isTierPremium && !userStore.isActiveReportSubscriber"
      id="need_help"
    >
      <iBaseHeader
        highlight-text="Not sure on what to do next?"
        :font-size-highlighted-text="headerSize"
        text="Talk to our career coach to figure out what exactly you need to do."
        font-size-text="14px"
        font-weight-text="500"
        :color-text="comet600"
        align="start"
      />
      <iCoachingUpsell />
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue"
import { useUserStore } from "@/stores/user"
import { useReportStore } from "@/stores/report"

// COMPOSABLES
import useIsScreenSize from "@/composables/useIsScreenSize"

// COMPONENTS
import iBaseHeader from "@/components/base/BaseHeader.vue"
import iProfessionalBio from "@/components/report/personality/ProfessionalBio.vue"
import iPersonalityAnalysis from "@/components/report/personality/PersonalityAnalysis.vue"
import iSelfImprovement from "@/components/report/personality/SelfImprovement.vue"
import iStrengthsAndWeaknesses from "@/components/report/personality/StrengthsAndWeaknesses.vue"
import iWorkplaceTraits from "@/components/report/personality/WorkplaceTraits.vue"
import iCoachingUpsell from "@/components/report/careers/CoachingUpsell.vue"

// ASSETS
import iconNeedHelpBlue from "@/assets/icons/icon-info-circle-blue.svg"
import iconNeedHelp from "@/assets/icons/icon-info-circle.svg"
import iconPersonalityActive from "@/assets/anchors/icon-file-active.svg"
import iconPersonality from "@/assets/anchors/icon-file.svg"
import iconBioActive from "@/assets/anchors/icon-user-square-active.svg"
import iconBio from "@/assets/anchors/icon-user-square.svg"
import iconStrengthsActive from "@/assets/anchors/icon-scales-active.svg"
import iconStrengths from "@/assets/anchors/icon-scales.svg"
import iconTraitsActive from "@/assets/anchors/icon-certificate-active.svg"
import iconTraits from "@/assets/anchors/icon-certificate.svg"
import iconBonusActive from "@/assets/anchors/icon-star-active.svg"
import iconBonus from "@/assets/anchors/icon-star.svg"

// STYLES
import { comet600 } from "@/styles/colors"
import { breakpointSm } from "@/styles/variables"

// TYPES
import type { PageAnchors } from "@/types/pageAnchors"

// EMITS
const emit = defineEmits(["load-anchors"])

// STORES AND COMPOSABLES
const userStore = useUserStore()
const reportStore = useReportStore()
const isNotMobile = useIsScreenSize(breakpointSm)

// COMPUTED
const headerSize = computed(() => (isNotMobile.value ? "36px" : "32px"))

// CONSTANTS
const CONSTANT_PAGE_ANCHORS: PageAnchors = {
  professional_bio: {
    text: "Professional Bio",
    icon: iconBio,
    iconActive: iconBioActive
  },
  personality_analysis: {
    text: "Personality Analysis",
    icon: iconPersonality,
    iconActive: iconPersonalityActive
  },
  strengths_weaknesses: {
    text: "Strength & Weaknesses",
    icon: iconStrengths,
    iconActive: iconStrengthsActive
  },
  bonus: {
    text: "Bonus",
    icon: iconBonus,
    iconActive: iconBonusActive
  },
  workplace_traits: {
    text: "Workplace Traits",
    icon: iconTraits,
    iconActive: iconTraitsActive,
    isHidden: !reportStore.hasWorkplaceTraitsItems
  },
  need_help: {
    text: "Need Help?",
    icon: iconNeedHelp,
    iconActive: iconNeedHelpBlue,
    isHidden: userStore.isTierPremium || userStore.isActiveReportSubscriber
  }
}

// LIFECYCLE HOOKS
onMounted(() => {
  emit("load-anchors", { anchors: CONSTANT_PAGE_ANCHORS, autoScroll: true })
})
</script>

<style scoped lang="scss">
.i-personality {
  max-width: 720px;
  margin: 0 auto;

  @include grid($cols: 1fr, $rows: auto, $gap: 56px);

  section {
    scroll-margin-top: 90px;
  }
}
</style>

<style lang="scss">
.i-personality {
  .no-mobile-break {
    margin-top: 32px;

    br {
      display: none;

      @media screen and (min-width: $breakpoint-sm) {
        display: block;
      }
    }
  }
}
</style>
